<script setup lang="ts">
import { computed } from "vue";
import type { ISatSetupProps } from "@chatfood/design-system";
import { SatSetup } from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { useBranding } from "~/composables/use-branding";
import { useLocale } from "~/composables/use-locale";
import { useAppURI } from "~/composables/use-app-uri";
import { ModalHub } from "~/components/modal-hub";
import { CheckoutCallback } from "~/components/checkout-callback";
import { NuxtRoutedPage } from "~/components/nuxt-routed-page";
import { CookiesConsent } from "~/components/cookies-consent";
import { ReviewForm } from "~/components/review-form";
import { useBusinessStore } from "~/stores/business";
import { onMounted, useAnalytics, useHead } from "#imports";

const css = bemBuilder("app");

const business = useBusinessStore();

const { t, setDefaultLanguage } = useLocale();
const { getHeadTags } = useBranding();

setDefaultLanguage(business.locale);

useHead(
  getHeadTags(
    business.designTokens,
    business.logo,
    business.styles?.customCssSheet,
  ),
);

const satelliteLocale = computed<ISatSetupProps["locale"]>(() => ({
  form_control: {
    optional_flag: t("app.satellite_defaults.form_control.optional_flag"),
    required_flag: t("app.satellite_defaults.form_control.required_flag"),
  },
  toast: {
    unexpected_error_message: t(
      "app.satellite_defaults.toast.unexpected_error_message",
    ),
    unexpected_error_title: t(
      "app.satellite_defaults.toast.unexpected_error_title",
    ),
  },
  menu_navigator: {
    search_placeholder: t(
      "app.satellite_defaults.menu_navigator.search_placeholder",
    ),
  },
  item_builder: {
    back_cta: t("app.satellite_defaults.item_builder.back_cta"),
    item: {
      cta_label: t("app.satellite_defaults.item_builder.item.cta_label"),
      note_label: t("app.satellite_defaults.item_builder.item.note_label"),
      note_placeholder: t(
        "app.satellite_defaults.item_builder.item.note_placeholder",
      ),
    },
    modifier: {
      cta_label: t("app.satellite_defaults.item_builder.modifier.cta_label"),
    },
  },
  item_handler: {
    allergens_label: t("app.satellite_defaults.item_handler.allergens_label"),
    bottle_deposit: t("app.satellite_defaults.item_handler.bottle_deposit"),
    calories_label: t("app.satellite_defaults.item_handler.calories_label"),
    calories_value: t("app.satellite_defaults.item_handler.calories_value"),
    choose_1: t("app.satellite_defaults.item_handler.choose_1"),
    choose_at_least_x: t(
      "app.satellite_defaults.item_handler.choose_at_least_x",
    ),
    choose_at_least_x_up_to_x: t(
      "app.satellite_defaults.item_handler.choose_at_least_x_up_to_x",
    ),
    choose_up_to_x: t("app.satellite_defaults.item_handler.choose_up_to_x"),
    customizable: t("app.satellite_defaults.item_handler.customizable"),
    min_selections_required: t(
      "app.satellite_defaults.item_handler.min_selections_required",
    ),
    product_info_cta_label: t(
      "app.satellite_defaults.item_handler.product_info_cta_label",
    ),
    info: {
      additives_label: t(
        "app.satellite_defaults.item_handler.info.additives_label",
      ),
      alcohol: t("app.satellite_defaults.item_handler.info.alcohol"),
      allergens_label: t(
        "app.satellite_defaults.item_handler.info.allergens_label",
      ),
      back_cta: t("app.satellite_defaults.item_handler.info.back_cta"),
      beverage_label: t(
        "app.satellite_defaults.item_handler.info.beverage_label",
      ),
      business_operator_label: t(
        "app.satellite_defaults.item_handler.info.business_operator_label",
      ),
      caffeine: t("app.satellite_defaults.item_handler.info.caffeine"),
      calories_value: t(
        "app.satellite_defaults.item_handler.info.calories_value",
      ),
      empty_label: t("app.satellite_defaults.item_handler.info.empty_label"),
      ingredients_label: t(
        "app.satellite_defaults.item_handler.info.ingredients_label",
      ),
      instructions_to_use_label: t(
        "app.satellite_defaults.item_handler.info.instructions_to_use_label",
      ),
      legal_name_label: t(
        "app.satellite_defaults.item_handler.info.legal_name_label",
      ),
      nutrition_label: t(
        "app.satellite_defaults.item_handler.info.nutrition_label",
      ),
      nutrition_table: {
        carbohydrates: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.carbohydrates",
        ),
        fat: t("app.satellite_defaults.item_handler.info.nutrition_table.fat"),
        grams_label: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.grams_label",
        ),
        net_quantity: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.net_quantity",
        ),
        protein: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.protein",
        ),
        salt: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.salt",
        ),
        saturated_fat: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.saturated_fat",
        ),
        serving_size: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.serving_size",
        ),
        sugar: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.sugar",
        ),
        title: t(
          "app.satellite_defaults.item_handler.info.nutrition_table.title",
        ),
      },
      prepackaged_label: t(
        "app.satellite_defaults.item_handler.info.prepackaged_label",
      ),
      prepackaged_label_no: t(
        "app.satellite_defaults.item_handler.info.prepackaged_label_no",
      ),
      prepackaged_label_yes: t(
        "app.satellite_defaults.item_handler.info.prepackaged_label_yes",
      ),
      range_calories_value: t(
        "app.satellite_defaults.item_handler.info.range_calories_value",
      ),
      reusable_label: t(
        "app.satellite_defaults.item_handler.info.reusable_label",
      ),
      reusable_label_no: t(
        "app.satellite_defaults.item_handler.info.reusable_label_no",
      ),
      reusable_label_yes: t(
        "app.satellite_defaults.item_handler.info.reusable_label_yes",
      ),
      storage_instructions_label: t(
        "app.satellite_defaults.item_handler.info.storage_instructions_label",
      ),
    },
  },
}));

onMounted(() => {
  if (business.isMobileApp) {
    document.documentElement.style.setProperty("--overscroll-behavior", "none");
  }

  useAppURI().persist();
  useAnalytics().trackEvent({
    name: "startJourney",
    payload: {},
  });
});
</script>

<template>
  <SatSetup :locale="satelliteLocale">
    <div :class="css()">
      <NuxtLayout>
        <NuxtRoutedPage />
      </NuxtLayout>
      <ModalHub />
      <CookiesConsent />
      <ReviewForm v-if="business.id" :business-id="business.id" />
    </div>
    <CheckoutCallback />
  </SatSetup>
</template>

<style lang="scss">
html,
body {
  /* to disable the overscroll bounce feature of browsers */
  overscroll-behavior: var(--overscroll-behavior, initial);
  -webkit-overflow-scrolling: auto;
}
</style>

<style lang="scss" scoped>
.app {
  min-width: 375px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  white-space: pre-line;

  @media (width <= $breakpoint-sm) {
    background-color: var(--color-base-100);
  }
}
</style>
